/**
 * i18next setup. Import once from main.tsx. Strings live in locales/*.json; `t("key")` is typed
 * against en.json (see i18next.d.ts), so a typo fails `tsc`.
 *
 * Language resolution: the signed-in user's `locale` field (client/src/lib/auth/auth-context.tsx sets
 * it), then localStorage, then the browser. Add a language: create locales/<code>.json, list it
 * in languages.ts (see there for everything else a language needs).
 */
import i18n, { type BackendModule, type ResourceKey } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { LOCALES, toLocale, type Locale } from "./languages";
import en from "@/i18n/locales/en.json";

export { isLocale, LOCALE_NAMES, LOCALES, toLocale, type Locale } from "./languages";
export const STORAGE_KEY = "locale";

/**
 * English ships with the app (fallback and types); every other language is its own chunk,
 * fetched the first time it is used (i18next backend below). main.tsx waits for `i18nReady`, so
 * the first paint is already in the user's language.
 */
export const LOCALE_LOADERS: Record<Exclude<Locale, "en">, () => Promise<{ default: unknown }>> = {
  de: () => import("@/i18n/locales/de.json"),
  fr: () => import("@/i18n/locales/fr.json"),
  es: () => import("@/i18n/locales/es.json"),
  it: () => import("@/i18n/locales/it.json"),
  nl: () => import("@/i18n/locales/nl.json"),
  pt: () => import("@/i18n/locales/pt.json"),
  pl: () => import("@/i18n/locales/pl.json"),
  ro: () => import("@/i18n/locales/ro.json"),
  cs: () => import("@/i18n/locales/cs.json"),
  hu: () => import("@/i18n/locales/hu.json"),
  sv: () => import("@/i18n/locales/sv.json"),
  da: () => import("@/i18n/locales/da.json"),
  nb: () => import("@/i18n/locales/nb.json"),
  fi: () => import("@/i18n/locales/fi.json"),
  is: () => import("@/i18n/locales/is.json"),
  et: () => import("@/i18n/locales/et.json"),
  lv: () => import("@/i18n/locales/lv.json"),
  lt: () => import("@/i18n/locales/lt.json"),
  tr: () => import("@/i18n/locales/tr.json"),
};

const lazyLocales: BackendModule = {
  type: "backend",
  init() {},
  read(language, _namespace, callback) {
    const load = LOCALE_LOADERS[language as Exclude<Locale, "en">];
    if (!load) return callback(null, {});
    load().then(
      (module) => callback(null, module.default as ResourceKey),
      (error: Error) => callback(error, false),
    );
  },
};

export const i18nReady = i18n
  .use(lazyLocales)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    fallbackLng: "en",
    supportedLngs: LOCALES,
    nonExplicitSupportedLngs: true,
    detection: {
      // "nb-NO", "no", "pt-BR", "de-AT" -> the supported base language.
      convertDetectedLanguage: (lng: string) => toLocale(lng) ?? lng,
      // ?lang=de from the website's links (the language of the page the visitor came from) wins
      // and is remembered like a choice in the language menu.
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lang",
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false }, // React escapes already
    returnNull: false,
  });

export default i18n;
